import { v4 } from 'uuid';

import type {
  GetPlanParameters,
  GetPlanSlugParameters,
  GetPlansParameters,
  ParsedWindcaveCreateSessionSchema,
  PlanSchema,
  PropertySchema,
  SessionType,
  ValidateUserDetailsResults,
} from '@energy-retailer/schema';
import type { QueryFunction } from '@tanstack/react-query';

const MINIMUM_QUERY_LENGTH = 6;

type AddressAutoCompleteQueryKey = [string, { query: string }];

export const getAddressAutoComplete: QueryFunction = async ({ queryKey }) => {
  const [, params] = queryKey as AddressAutoCompleteQueryKey;

  const { query } = params;

  if (query.length < MINIMUM_QUERY_LENGTH) {
    return {};
  }

  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/address/search?address=${query}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
};

type PlanQueryKey = [string, GetPlanParameters];

export const getPlan: QueryFunction = async ({
  queryKey,
}): Promise<PlanSchema | null> => {
  const [, params] = queryKey as PlanQueryKey;

  if (!params.planID) {
    return null;
  }

  const searchParams = new URLSearchParams(params);

  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/plan?${searchParams.toString()}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
};

type PlanSlugQueryKey = [string, GetPlanSlugParameters];

export const getPlanBySlug: QueryFunction = async ({
  queryKey,
}): Promise<PlanSchema | null> => {
  const [, params] = queryKey as PlanSlugQueryKey;

  if (!params.slug) {
    return null;
  }

  const {
    customerType,
    distributor,
    energyType,
    postcode,
    ntc,
    promotionId,
    slug,
  } = params as unknown as Record<string, string>;

  const searchParams = new URLSearchParams({
    customerType,
    distributor,
    energyType,
    postcode,
    ntc,
    ...(slug ? { slug } : {}),
    ...(promotionId ? { promotionId } : {}),
  });

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/plan?${searchParams.toString()}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
};

type PlansQueryKey = [string, GetPlansParameters];

export const getPlans: QueryFunction = async ({
  queryKey,
}): Promise<PlanSchema[]> => {
  const [, params] = queryKey as PlansQueryKey;

  const { customerType, distributor, energyType, postcode, ntc, promotionId } =
    params as unknown as Record<string, string>;

  const searchParams = new URLSearchParams({
    customerType,
    distributor,
    energyType,
    postcode,
    ntc,
    ...(promotionId ? { promotionId } : {}),
  });

  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/plans?${searchParams.toString()}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
};

export const getAddressDetails = async (
  key: string
): Promise<PropertySchema[]> => {
  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/address/details/${key}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
    }
  );

  if (response.status === 400) {
    return [];
  }

  const data = (await response.json()) as PropertySchema[];

  return data;
};

type AddressFormatQueryKey = [string, { key: string; fullAddress: string }];

export const getAddressDetailsQuery: QueryFunction = async ({
  queryKey,
}): Promise<PropertySchema[]> => {
  const [, params] = queryKey as AddressFormatQueryKey;

  const { key } = params;

  return getAddressDetails(key);
};

export const getNmiDetails = async (key: string): Promise<PropertySchema> => {
  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/msats/details/${key}`,
    // `https://b2c-public-website-dev.powershop.com.au/api/msats/details/${key}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
      body: undefined,
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json() as unknown as PropertySchema;
};

export const getCreatedSession =
  async (): Promise<ParsedWindcaveCreateSessionSchema> => {
    const response = await window.AwsWafIntegration.fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/windcave/create`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'x-correlation-id': v4(),
          'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json() as unknown as ParsedWindcaveCreateSessionSchema;
  };

export const getSession = async ({
  id,
}: {
  id?: string;
}): Promise<SessionType | undefined> => {
  if (!id) {
    return undefined;
  }

  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/windcave/session/${id}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json() as unknown as SessionType;
};

export const validateUserDetails = async ({
  params,
}: {
  params: {
    email: string;
    givenName: string;
    familyName: string;
    birthDate: string;
  };
}): Promise<ValidateUserDetailsResults | number> => {
  const response = await window.AwsWafIntegration.fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/flux/validate-user-details`,
    {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-correlation-id': v4(),
        'x-session-id': window.sessionStorage.getItem('sessionId') ?? '',
      },
    }
  );

  if (!response.ok) {
    return response.status;
  }

  return response.json() as unknown as ValidateUserDetailsResults;
};
